import { getApiUrl, getAuthHeaders } from '../config/api';
import { API_CONFIG } from '../config/api';

export interface Banner {
  id: number;
  title: string;
  slug: string;
  image: string;
  description?: string;
  linkUrl?: string;
  sortOrder: number;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBannerData {
  title: string;
  slug: string;
  description?: string;
  linkUrl?: string;
  sortOrder?: number;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface UpdateBannerData {
  title?: string;
  slug?: string;
  description?: string;
  linkUrl?: string;
  sortOrder?: number;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface BannerResponse {
  success: boolean;
  data: Banner;
  message?: string;
}

export interface BannersListResponse {
  success: boolean;
  data: {
    banners: Banner[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
  message?: string;
}

export interface BannerFilters {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'title' | 'sortOrder' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

class BannerService {
  // Get all banners with pagination and filtering
  async getBanners(filters: BannerFilters = {}): Promise<BannersListResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `${getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_BANNERS)}?${params.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch banners');
    }

    return response.json();
  }

  // Get single banner by ID
  async getBanner(id: number): Promise<BannerResponse> {
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_BANNERS}/${id}`), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch banner');
    }

    return response.json();
  }

  // Create new banner
  async createBanner(data: CreateBannerData, imageFile?: File | null): Promise<BannerResponse> {
    const formData = new FormData();
    
    // Append all form data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Append image file if provided
    if (imageFile) {
      formData.append('image', imageFile);
    }

    const token = localStorage.getItem('admin_token');
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_BANNERS), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // ...getAuthHeaders(),
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create banner');
    }

    return response.json();
  }

  // Update banner
  async updateBanner(id: number, data: UpdateBannerData, imageFile?: File | null): Promise<BannerResponse> {
    const formData = new FormData();
    
    // Append all form data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Append image file if provided
    if (imageFile) {
      formData.append('image', imageFile);
    }

    const token = localStorage.getItem('admin_token');
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_BANNERS}/${id}`), {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        // ...getAuthHeaders(),
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update banner');
    }

    return response.json();
  }

  // Delete banner
  async deleteBanner(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_BANNERS}/${id}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete banner');
    }

    return response.json();
  }

  // Update banner status
  async updateBannerStatus(id: number, isActive: boolean): Promise<BannerResponse> {
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_BANNERS}/${id}/status`), {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update banner status');
    }

    return response.json();
  }

  // Bulk update banners
  async bulkUpdateBanners(bannerIds: number[], updateData: Partial<UpdateBannerData>): Promise<{ success: boolean; message: string; data: { affectedCount: number } }> {
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_BANNERS}/bulk`), {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ bannerIds, updateData }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to bulk update banners');
    }

    return response.json();
  }
}

export const bannerService = new BannerService();
